import React from 'react';
import { formatRate } from '~containers/analytics/kpis/format-rate';
import { BLUE_700 } from '~styles/colors';
import style from './share-bars.module.scss';

export interface ShareBar {
    key: string;
    label: string;
    value: number;
    /** What this bar's share is a share of, in place of the bars' base. */
    of?: number;
    /** The whole the other bars are a share of: only its count is written. */
    isBase?: boolean;
    /** Muted text after the figures, such as a second count. */
    detail?: string;
}

export interface ShareBarsProps {
    bars: ShareBar[];
    /** What each share is a share of. Omitted, it is the sum of the bars. */
    base?: number;
    /**
     * `share`: each bar is as long as its share, for rates that do not add
     * up. Omitted, the longest bar fills the track.
     */
    widthBy?: 'share';
    color?: string;
    /** Said in place of the bars when there are none. */
    empty: string;
}

/**
 * Horizontal bars, each labelled with its count and its share, in the order
 * the caller gives them. The figures are written out, so no hover
 * is needed to read them.
 */
export const ShareBars = ({
    bars,
    base,
    widthBy,
    color = BLUE_700,
    empty,
}: ShareBarsProps) => {
    if (bars.length === 0) {
        return <p className={style.empty}>{empty}</p>;
    }
    const whole = base ?? bars.reduce((total, bar) => total + bar.value, 0);
    const longest = Math.max(...bars.map((bar) => bar.value));
    return (
        <ul className={style.bars}>
            {bars.map((bar) => {
                const of = bar.of ?? whole;
                const barShare = of > 0 && !bar.isBase ? bar.value / of : null;
                const width =
                    widthBy === 'share'
                        ? barShare ?? 0
                        : longest > 0
                        ? bar.value / longest
                        : 0;
                return (
                    <li key={bar.key} className={style.bar_row}>
                        <div className={style.bar_text}>
                            <span className={style.bar_label}>{bar.label}</span>
                            <span className={style.bar_figures}>
                                {bar.value.toLocaleString()}
                                {barShare !== null && (
                                    <span className={style.bar_share}>
                                        {formatRate(barShare)}
                                    </span>
                                )}
                                {bar.detail && (
                                    <span className={style.bar_share}>
                                        {bar.detail}
                                    </span>
                                )}
                            </span>
                        </div>
                        <div className={style.track} aria-hidden="true">
                            <div
                                className={style.fill}
                                style={{
                                    width: `${Math.min(width, 1) * 100}%`,
                                    background: color,
                                }}
                            />
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};
